import clsx from "clsx";
import {
  CSSProperties,
  memo,
  ReactNode,
  startTransition,
  useMemo,
  useState,
} from "react";
import ReceivedTransactionCard from "./ReceivedTransactionCard";
import PendingTransactionCard from "./PendingTransactionCard";
import ReceivedContactInviteCard from "./ReceivedContactInviteCard";
import SendContactInviteCard from "./SendContactInviteCard";
import AcceptedContactInviteCard from "./AcceptedContactInviteCard";
import AccountCard from "./AccountCard";
import SentContactInviteCard from "./SentContactInviteCard";
import StandardTransactionCard from "./StandardTransactionCard";
import FileCard from "./FileCard";
import ExtensionCard from "./ExtensionCard";
import useMobile from "src/hooks/useMobile";
import { useSwipeable } from "react-swipeable";
import AvailablePerCurrencyCard from "./AvailablePerCurrencyCard";
import { ALLOW_SWIPE_IN_DESKTOP } from "src/helpers/constants";
import TransactionsPerCurrencyCard from "./TransactionsPerCurrencyCard";
import AccountCardV2 from "./AccountCardV2";
import ContactInfoCard from "./ContactInfoCard";
import AutoBankCard from "../../auto/components/AutoBankCard/AutoBankCard";
import BankCard from "../BankCard/BankCard";
import StatsSummaryCard from "./StatsSummaryCard";

const SWIPE_DELTA = 150;

interface ICards {
  children: ReactNode;
  columns?: number;
  mobileColumns?: number;
  className?: string;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

type TCards = {
  ReceivedTransaction: typeof ReceivedTransactionCard;
  PendingTransaction: typeof PendingTransactionCard;
  AcceptContactInvite: typeof ReceivedContactInviteCard;
  SendContactInvite: typeof SendContactInviteCard;
  AcceptedContactInvite: typeof AcceptedContactInviteCard;
  SentContactInvite: typeof SentContactInviteCard;
  AccountCard: typeof AccountCard;
  AccountCardV2: typeof AccountCardV2;
  StandardTransaction: typeof StandardTransactionCard;
  FileCard: typeof FileCard;
  ExtensionCard: typeof ExtensionCard;
  AvailablePerCurrency: typeof AvailablePerCurrencyCard;
  TransactionsPerCurrency: typeof TransactionsPerCurrencyCard;
  ContactInfo: typeof ContactInfoCard;
  AutoBankCard: typeof AutoBankCard;
  BankCard: typeof BankCard;
  StatsSummaryCard: typeof StatsSummaryCard;
};

const Cards: React.FC<ICards> & TCards = ({
  children,
  columns = 1,
  mobileColumns = 1,
  className,
  onSwipeLeft,
  onSwipeRight,
}) => {
  const { isMobile } = useMobile();
  const [translateX, setTranslateX] = useState<number>(0);

  const isSwipeable = useMemo(() => {
    return (
      !!onSwipeLeft &&
      !!onSwipeRight &&
      children &&
      (isMobile ? true : ALLOW_SWIPE_IN_DESKTOP)
    );
  }, [onSwipeLeft, onSwipeRight, isMobile]);

  const handlers = useSwipeable({
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: true,
    onSwiping: ({ deltaX, dir }) => {
      if (dir === "Left" || dir === "Right") {
        startTransition(() => {
          setTranslateX(deltaX);
        });
      }
    },
    onSwiped: (eventData) => {
      const { dir, deltaX } = eventData;
      if (["Left", "Right"].includes(dir)) {
        startTransition(() => {
          if (Math.abs(deltaX) > SWIPE_DELTA) {
            if (dir === "Left") onSwipeLeft?.();
            if (dir === "Right") onSwipeRight?.();
          }
          setTranslateX(0);
        });
      }
    },
  });

  return (
    <div
      className={clsx([
        "grid",
        `[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]`,
        `md:[grid-template-columns:repeat(var(--cols-md),minmax(0,1fr))]`,
        // "2xl:grid-cols-3",
        "md:gap-1",
        "gap-1",
        className,
        {
          "cursor-grab": isSwipeable,
        },
      ])}
      {...(isSwipeable ? handlers : {})}
      style={
        {
          "--cols-md": columns,
          "--cols": mobileColumns,
          ...(isSwipeable
            ? {
                touchAction: "pan-y",
                transform: `translateX(${translateX}px)`,
                transition: translateX === 0 ? "transform 0.2s ease" : "none",
              }
            : {}),
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
};

Cards.ReceivedTransaction = memo(ReceivedTransactionCard);
Cards.PendingTransaction = memo(PendingTransactionCard);
Cards.AcceptContactInvite = memo(ReceivedContactInviteCard);
Cards.SendContactInvite = memo(SendContactInviteCard);
Cards.AcceptedContactInvite = memo(AcceptedContactInviteCard);
Cards.SentContactInvite = memo(SentContactInviteCard);
Cards.AccountCard = memo(AccountCard);
Cards.AccountCardV2 = memo(AccountCardV2);
Cards.StandardTransaction = memo(StandardTransactionCard);
Cards.FileCard = memo(FileCard);
Cards.ExtensionCard = memo(ExtensionCard);
Cards.AvailablePerCurrency = memo(AvailablePerCurrencyCard);
Cards.TransactionsPerCurrency = TransactionsPerCurrencyCard;
Cards.ContactInfo = ContactInfoCard;
Cards.AutoBankCard = AutoBankCard;
Cards.BankCard = BankCard;
Cards.StatsSummaryCard = StatsSummaryCard;

export default Cards;
